import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { insertNewsItemSchema, NewsItem } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { AudienceSelector } from "@/components/ui/AudienceSelector";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CalendarIcon, LoaderCircle } from "lucide-react";
import { VoiceInputButton } from "@/components/common/VoiceInput";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Create a separate schema for the form input
const newsFormInputSchema = z.object({
  title: z.string().min(5, { message: "Title must be at least 5 characters" }),
  content: z.string().min(10, { message: "Content must be at least 10 characters" }),
  department: z.string().nullable(),
  tags: z.string().optional(),
  imageUrl: z.string().optional().nullable(),
  // Use string format for publish date to match API expectations
  publishDate: z.date(),
  authorId: z.number(),
  // Team visibility
  visibleToAllTeams: z.boolean().default(true),
  teamIds: z.array(z.number()).default([]),
});

type NewsFormInput = z.infer<typeof newsFormInputSchema>;

// Quill editor modules/formats
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link', 'image', 'video'],
    [{ color: [] }, { background: [] }],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'link', 'image', 'video',
  'color', 'background',
];

interface CreateNewsFormProps {
  isOpen: boolean;
  onClose: () => void;
  newsItem?: NewsItem;
}

export default function CreateNewsForm({ isOpen, onClose, newsItem }: CreateNewsFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!newsItem;

  const form = useForm<NewsFormInput>({
    resolver: zodResolver(newsFormInputSchema),
    defaultValues: {
      title: newsItem?.title || "",
      content: newsItem?.content || "",
      department: newsItem?.department || user?.department || null,
      tags: newsItem?.tags ? newsItem.tags.join(", ") : "",
      imageUrl: newsItem?.imageUrl || null,
      publishDate: newsItem ? new Date(newsItem.publishDate) : new Date(),
      authorId: newsItem?.authorId || user?.id || 0,
      visibleToAllTeams: newsItem?.visibleToAllTeams ?? true,
      teamIds: [],
    },
  });

  const createNewsMutation = useMutation({
    mutationFn: async (formData: NewsFormInput) => {
      // Transform form data to the expected format for the API
      const data = {
        title: formData.title,
        content: formData.content,
        department: formData.department,
        imageUrl: formData.imageUrl,
        // Convert Date object to ISO string for API
        publishDate: formData.publishDate.toISOString(),
        authorId: formData.authorId,
        // Convert comma-separated tags to array
        tags: formData.tags ? formData.tags.split(',').map((tag: string) => tag.trim()) : null,
        visibleToAllTeams: formData.visibleToAllTeams,
      };

      const method = isEditing ? "PUT" : "POST";
      const url = isEditing ? `/api/news/${newsItem!.id}` : "/api/news";
      const result = await apiRequest(method, url, data);

      // If specific teams are selected, set the team visibility
      if (!formData.visibleToAllTeams && formData.teamIds.length > 0) {
        await apiRequest("POST", `/api/news/${result.id}/teams`, {
          teamIds: formData.teamIds,
        });
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/news'] });
      toast({
        title: "Success!",
        description: `News post ${isEditing ? 'updated' : 'created'} successfully.`,
        variant: "default",
      });
      form.reset();
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });

  const onSubmit = (data: NewsFormInput) => {
    setIsSubmitting(true);
    createNewsMutation.mutate(data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[725px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{isEditing ? "Edit News Post" : "Create News Post"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update this news post." : "Share company updates, announcements, and important information."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 py-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Post title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Content</FormLabel>
                    <VoiceInputButton
                      variant="toolbar"
                      onTranscript={(text) => {
                        const current = form.getValues("content") || "";
                        form.setValue("content", current + `<p>${text}</p>`, { shouldValidate: true });
                      }}
                    />
                  </div>
                  <FormControl>
                    <div className="quill-container">
                      <Controller
                        name="content"
                        control={form.control}
                        render={({ field }) => (
                          <ReactQuill
                            theme="snow"
                            value={field.value}
                            onChange={field.onChange}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Write your post content here..."
                            className="min-h-[200px]"
                          />
                        )}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Engineering">Engineering</SelectItem>
                        <SelectItem value="Product">Product</SelectItem>
                        <SelectItem value="HR">HR</SelectItem>
                        <SelectItem value="Sales">Sales</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Finance">Finance</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="publishDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Publish Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags (comma separated)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="company, announcement, update"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="imageUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Featured Image URL (optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="https://example.com/image.jpg" 
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Audience Selection */}
            <div className="border-t pt-6 mt-2">
              <AudienceSelector
                selectedTeamIds={form.watch("teamIds")}
                onTeamIdsChange={(teamIds) => form.setValue("teamIds", teamIds)}
                visibleToAllTeams={form.watch("visibleToAllTeams")}
                onVisibleToAllTeamsChange={(value) => form.setValue("visibleToAllTeams", value)}
                label="Who should see this post?"
                description="Control which teams can view this news post"
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                {isEditing ? "Save Changes" : "Publish Post"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}